import { CHART_OBJECTS_SETTINGS_STORAGE_KEYS } from "@/lib/localStorageKeys/chart-objects";
import type { DrawingsList } from "./types";

export const getStoredDrawings = (): DrawingsList => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const storedData = localStorage.getItem(
      CHART_OBJECTS_SETTINGS_STORAGE_KEYS.OBJECTS
    );

    if (!storedData) {
      return [];
    }

    const parsedData = JSON.parse(storedData);
    return Array.isArray(parsedData) ? parsedData : [];
  } catch (error) {
    console.error("Error loading chart drawings from localStorage:", error);
    return [];
  }
};

export const saveDrawings = (objects: DrawingsList): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(
      CHART_OBJECTS_SETTINGS_STORAGE_KEYS.OBJECTS,
      JSON.stringify(objects)
    );
  } catch (error) {
    console.error("Error saving chart drawings to localStorage:", error);
  }
};

export function getStoredDrawingsSize(): number {
  if (typeof window === "undefined") return 0;

  try {
    const stored = localStorage.getItem(
      CHART_OBJECTS_SETTINGS_STORAGE_KEYS.OBJECTS
    );
    if (!stored) return 0;
    return stored.length * 2;
  } catch (error) {
    console.error("Error getting chart drawings size:", error);
    return 0;
  }
}

export function clearStoredDrawings(): boolean {
  if (typeof window === "undefined") return false;

  try {
    localStorage.removeItem(CHART_OBJECTS_SETTINGS_STORAGE_KEYS.OBJECTS);
    return true;
  } catch (error) {
    console.error("Error clearing chart drawings:", error);
    return false;
  }
}

let drawingsStorageListenerAttached = false;

/**
 * Subscribes to cross-window `storage` events for the drawings key so that
 * changes made in one window (main app or chart popup) are reflected in all
 * other windows. The `storage` event only fires in windows OTHER than the one
 * that performed the write, and `onChange` should only read (not save), so no
 * feedback loop is possible. Idempotent: attaches at most one listener.
 */
export const subscribeToDrawingsStorageChanges = (
  onChange: () => void
): void => {
  if (typeof window === "undefined" || drawingsStorageListenerAttached) {
    return;
  }

  drawingsStorageListenerAttached = true;

  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key === null || event.key === CHART_OBJECTS_SETTINGS_STORAGE_KEYS.OBJECTS) {
      onChange();
    }
  });
};

/** @deprecated */
export const getStoredChartObjects = getStoredDrawings;
/** @deprecated */
export const saveChartObjects = saveDrawings;
/** @deprecated */
export const getStoredChartObjectsSize = getStoredDrawingsSize;
/** @deprecated */
export const clearStoredChartObjects = clearStoredDrawings;

/** @deprecated alias used by storage/clear.ts */
export const clearStoredChartObjectsSettings = clearStoredDrawings;
